export const articles = [
  {
    slug: "how-is-kawei-calculated",
    title: "咖位是怎么被算出来的：一套可复用的四维打分法",
    category: "咖位方法论",
    excerpt:
      "「咖位」听起来像玄学，但它其实可以被拆成几个可观察、可打分的维度。这篇文章把我们内部使用的四维评估框架完整讲清楚，你也可以拿它去复盘任何一次同框。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Editorial%20desk%20scene%20with%20a%20hand%20drawn%20radar%20chart%20on%20cream%20paper%2C%20brass%20ruler%20and%20amber%20ink%20pen%20resting%20beside%2C%20warm%20natural%20window%20light%2C%20deep%20red%20cloth%20accent%2C%20no%20people%2C%20refined%20magazine%20flatlay%20photography&width=1200&height=800&seq=kawei-art-01&orientation=landscape",
    author: "咖位观察局 · 编辑部",
    date: "2026-08-26",
    readTime: "8 分钟",
    tags: ["咖位方法论", "评估框架", "四维打分"],
    views: 41200,
    sections: [
      {
        heading: "一、先承认：咖位是一张动态的位置图",
        paragraphs: [
          "很多人把咖位理解成一个固定的名次，好像只要排出来就一劳永逸。但真实的行业里，位置是流动的：一部剧的播出、一场直播的翻车、一次代言的头衔变化，都可能让同一个人的位置在几个月内发生明显位移。",
          "所以我们更愿意把咖位定义成「在特定时间段内，一个人在行业里被认可的位置」。既然是动态的，就必须带上时间和场景两个限定词，否则任何结论都站不住脚。",
        ],
      },
      {
        heading: "二、四个维度：热度、话题、商业、专业",
        paragraphs: [
          "我们把可观察的信号归纳成四个维度。热度指关注度的绝对量级，体现在搜索、播放、讨论的规模上；话题指带动他人讨论的能力，衡量的是影响力的外溢；商业指可被定价的程度，代言层级、合作报价、带货转化都属于这一类；专业指在本职赛道内被同行认可的程度。",
          "四个维度并不是简单加总。它们更像四种货币，各自在自己的市场里流通，需要根据场景决定权重。一场以转化为目标的直播，商业维度的权重自然更高；一次颁奖典礼，专业与形象的权重则会上来。",
        ],
        list: [
          "热度：搜索量级、内容播放规模、讨论总盘",
          "话题：带动讨论的能力、二次传播的深度",
          "商业：代言层级、合作报价、转化结构",
          "专业：同行认可、作品口碑、赛道资历",
        ],
      },
      {
        heading: "三、为什么不能只比一个数字",
        paragraphs: [
          "只比热度，会得出「谁粉丝多谁就赢」的粗暴结论；只比专业，又会忽略行业真实的商业逻辑。更麻烦的是，单一数字极易被短期事件扭曲，一条热搜就能让热度维度瞬间跳升，但这不代表位置真的变了。",
          "我们的做法是让四个维度同时亮相，把分歧摊开给你看。当一张对比图里出现「热度更高、但专业更低」这样的反差时，读者的第一反应往往是「为什么会这样」，而这正是思考的起点。",
        ],
      },
      {
        heading: "四、把框架用到你自己的观察里",
        paragraphs: [
          "你不需要认同我们的权重设置，甚至可以把维度换成你关心的那四项。重要的是养成一个习惯：在给出「谁更火」的判断之前，先问清楚「在哪个场景、用什么标准、看的是哪段时间」。",
          "当这三个问题有了答案，你会发现很多看似无解的争论，其实只是双方在用不同的尺子量同一个东西。",
        ],
      },
    ],
  },
  {
    slug: "three-details-in-live-room",
    title: "直播间里的话语权：镜头之外的三个细节",
    category: "同框解析",
    excerpt:
      "一场嘉宾同台的直播，谁在主导节奏，往往不体现在谁话多，而藏在接话的时机、看镜头的方向和被反问后的反应里。三个细节，帮你看懂话语权的分配。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Close%20up%20editorial%20shot%20of%20two%20microphones%20on%20a%20warm%20walnut%20table%20in%20a%20livestream%20studio%2C%20soft%20amber%20ring%20light%20glow%20in%20background%2C%20deep%20crimson%20curtain%20blurred%2C%20no%20people%2C%20cinematic%20magazine%20photography%2C%20warm%20minimal%20tone&width=1200&height=800&seq=kawei-art-02&orientation=landscape",
    author: "咖位观察局 · 内容组",
    date: "2026-08-19",
    readTime: "7 分钟",
    tags: ["直播同框", "话语权", "细节观察"],
    views: 36800,
    sections: [
      {
        heading: "话语权不等于话多",
        paragraphs: [
          "很多人误以为直播间里说得最多的人就是主导者。但真正的话语权体现在「谁决定话题的走向」。一个人可以话很少，却每次开口都能把节奏拉回来，这才是掌控。",
          "观察一场同台直播时，先别急着数谁说得多，而是看话题在被谁牵引。当嘉宾把话题带偏，是谁自然地把场子收回来，那个人往往才是真正的主导者。",
        ],
      },
      {
        heading: "细节一：接话的时机",
        paragraphs: [
          "接话太快会显得抢，太慢又会让场子冷下来。有经验的人会在对方话音刚落、但情绪还悬着的那个瞬间接上，既承接了氛围，又顺势推进。这个时间差非常短，普通人几乎察觉不到，但看回放时会很明显。",
        ],
      },
      {
        heading: "细节二：看镜头的方向",
        paragraphs: [
          "谁在说话时习惯性看镜头，谁更常看身边的人，透露的是不同的定位。看镜头的人把自己当成「对观众说话」的一方，看身边的人则更接近「和现场交流」的状态。两种没有高下之分，但决定了观众会觉得谁更像主角。",
        ],
      },
      {
        heading: "细节三：被反问之后的反应",
        paragraphs: [
          "最难伪装的是被反问的那一瞬间。有人会下意识地把问题抛回去，有人会顺着问题展开，也有人会短暂停顿后再接。停顿本身不可怕，可怕的是停顿之后把话题彻底丢掉了。能稳稳接住反问的人，通常在自己的主场里练过很多次。",
          "把这三个细节连起来看，你会发现所谓的话语权并不神秘，它是一连串微小选择累积出来的结果。",
        ],
      },
    ],
  },
  {
    slug: "three-scorecards-no-exchange-rate",
    title: "点赞、票房、收视：三套成绩单没有统一汇率",
    category: "流量观察",
    excerpt:
      "把不同赛道的成绩单放在一起比较，是讨论里最常见的误区。点赞、票房、收视背后是三套完全不同的评价体系，强行换算只会得出失真的结论。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Editorial%20conceptual%20still%20life%20of%20three%20different%20measuring%20tools%20side%20by%20side%20on%20a%20cream%20surface%2C%20a%20brass%20scale%2C%20a%20wooden%20ruler%20and%20a%20small%20clock%2C%20warm%20amber%20light%2C%20no%20people%2C%20minimal%20magazine%20photography&width=1200&height=800&seq=kawei-art-03&orientation=landscape",
    author: "咖位观察局 · 数据组",
    date: "2026-08-12",
    readTime: "6 分钟",
    tags: ["流量观察", "评价体系", "数据口径"],
    views: 29400,
    sections: [
      {
        heading: "三套体系，三种口径",
        paragraphs: [
          "点赞是即时反馈，成本极低，一个人可以在几秒内完成；票房需要真金白银，受排片、档期、口碑多重影响；收视则介于两者之间，既需要打开电视，又不必额外付费。三者的门槛和含义完全不同。",
          "当我们说「某某的点赞比某某的票房更亮眼」时，其实是在用两把不同的尺子量两个不同的对象。这种比较在情绪上很爽，在逻辑上却站不住。",
        ],
      },
      {
        heading: "为什么会忍不住比较",
        paragraphs: [
          "因为数字自带说服力。一个六位数、一个七位数，摆在一起高下立判，大脑会自动完成排序。但我们往往忽略了单位，忽略了统计口径，也忽略了数字背后的行为成本。",
          "理解这一点之后，看到「谁的数据更炸」这类讨论，第一反应应该是问：这是哪一套体系里的数字？它的门槛是什么？",
        ],
      },
      {
        heading: "正确的做法是分赛道看趋势",
        paragraphs: [
          "比起跨赛道横向比较，更有价值的是同赛道纵向看趋势。一个人在自己的赛道里，数据是稳步上升还是持续下滑，远比跨赛道的高低更能说明问题。",
          "我们在月度热度走势板块里坚持分线呈现，就是为了避免把所有数据揉成一个总分，让读者失去判断的锚点。",
        ],
      },
    ],
  },
  {
    slug: "levels-of-endorsement-titles",
    title: "代言头衔的等级学：从全球代言到品牌挚友",
    category: "商业价值",
    excerpt:
      "同一份合作协议，头衔的差别可能比价格更大。全球代言人、区域代言、品牌大使、挚友，这些称谓背后是一套清晰的层级逻辑，也和合作目标强相关。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Luxury%20editorial%20flatlay%20of%20layered%20elegant%20name%20cards%20stacked%20at%20varying%20heights%20on%20deep%20red%20velvet%2C%20soft%20golden%20light%20sweeping%20across%20embossed%20paper%20texture%2C%20no%20legible%20text%2C%20refined%20fashion%20magazine%20photography&width=1200&height=800&seq=kawei-art-04&orientation=landscape",
    author: "咖位观察局 · 商业组",
    date: "2026-08-04",
    readTime: "7 分钟",
    tags: ["品牌代言", "头衔体系", "商业价值"],
    views: 32100,
    sections: [
      {
        heading: "头衔是一份公开的位置声明",
        paragraphs: [
          "品牌在官宣时给出什么头衔，本质上是一次公开的位置声明。它既是对外传递合作关系的方式，也是对内统一资源投入的标尺。头衔越高，通常意味着更长的合作周期、更广的露出范围。",
        ],
      },
      {
        heading: "层级的大致顺序",
        paragraphs: [
          "虽然各家品牌的叫法不完全统一，但大致的顺序是可以归纳的。全球或大中华区级别的代言处于顶层，区域代言在其下，再往下是品牌大使，最轻量的是挚友与单次合作。不同层级对应不同的权益包与曝光资源。",
        ],
        list: [
          "全球 / 大中华区代言人：最高层级，周期长、范围广",
          "区域或品类代言：限定市场或产品线",
          "品牌大使：形象为主，权益相对灵活",
          "品牌挚友 / 单次合作：轻量试水，侧重即时转化",
        ],
      },
      {
        heading: "轻量合作不是降级",
        paragraphs: [
          "看到挚友级别的合作，不必立刻得出「地位下滑」的结论。很多品牌会先用轻量合作测试人群匹配度与转化效率，数据表现好再逐级加码。这更像一次试水，而不是最终评级。",
          "理解这套逻辑之后，再看同品牌的合作名单，你会更关注「谁在核心位置、谁在测试位置」，而不是简单地用一个头衔高下去评判。",
        ],
      },
    ],
  },
  {
    slug: "decode-shared-frame-moments",
    title: "同框名场面拆解：站位、朝向与谁先开口",
    category: "同框解析",
    excerpt:
      "合影的两秒里藏着大量信息。谁站中间、谁的朝向更正面、谁先开口打招呼，这些看似随机的动作，其实高度依赖现场的位置关系与主持流程。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20scene%20of%20three%20empty%20elegant%20chairs%20arranged%20on%20a%20warm%20stage%20with%20a%20soft%20amber%20spotlight%20pool%2C%20deep%20crimson%20backdrop%20blurred%2C%20no%20people%2C%20cinematic%20magazine%20photography%2C%20balanced%20composition&width=1200&height=800&seq=kawei-art-05&orientation=landscape",
    author: "咖位观察局 · 内容组",
    date: "2026-07-27",
    readTime: "6 分钟",
    tags: ["同框解析", "站位", "位置学"],
    views: 26800,
    sections: [
      {
        heading: "合影不是随便站的",
        paragraphs: [
          "活动现场的合影，站位通常由工作人员在几秒内完成安排，遵循的是一套默认的行业惯例。谁站中间、谁站两侧，往往和当天的活动主题、主办方意愿以及抵达顺序都有关系。",
          "所以看到一张合影，先别急着解读「谁被冷落」，先想想这是一场什么性质的活动、主办方想突出谁，会得到更接近事实的判断。",
        ],
      },
      {
        heading: "朝向也是一种信息",
        paragraphs: [
          "面向镜头、身体略微侧转、视线落在别处，这些朝向差异在画面上会被放大。站位居中且正面朝向镜头的人，视觉上自然成为焦点，这既是安排的结果，也会强化后续的讨论。",
        ],
      },
      {
        heading: "谁先开口，取决于流程",
        paragraphs: [
          "在人多的场合，谁先开口打招呼，很多时候由主持人引导或流程决定，并不能直接等同于位置高低。把流程因素排除之后，剩下的才是真正值得关注的互动细节。",
          "理性看待同框，关键是把「安排」和「自发」区分开。属于安排的，不必过度解读；属于自发的，才更接近真实的关系。",
        ],
      },
    ],
  },
  {
    slug: "long-term-operators-three-moves",
    title: "流量退潮之后，长期主义者的三个共同动作",
    category: "流量观察",
    excerpt:
      "短期爆红靠的是单点事件，长期站位靠的是持续经营。复盘那些在热度退潮后依然稳定的人，会发现他们在内容节奏、受众结构与合作选择上有高度相似的共同动作。",
    coverImage:
      "https://readdy.ai/api/search-image?query=Serene%20editorial%20landscape%20of%20calm%20sea%20water%20after%20the%20tide%20recedes%20with%20warm%20amber%20sunset%20light%20reflecting%20on%20gentle%20ripples%2C%20soft%20crimson%20gradient%20sky%2C%20minimal%20refined%20magazine%20photography&width=1200&height=800&seq=kawei-art-06&orientation=landscape",
    author: "咖位观察局 · 数据组",
    date: "2026-07-15",
    readTime: "7 分钟",
    tags: ["流量观察", "长期经营", "基本盘"],
    views: 23500,
    sections: [
      {
        heading: "动作一：把内容节奏当成工程",
        paragraphs: [
          "爆红的人往往依赖一次高光事件，而长期稳定的人会把更新频率、选题结构、互动方式流程化。他们不一定每天都制造爆款，但几乎不会出现长时间的内容空窗。",
        ],
      },
      {
        heading: "动作二：主动优化受众结构",
        paragraphs: [
          "粉丝数量的增长并不等于基本盘的稳固。真正拉开差距的是受众结构：是高度依赖单一情绪的围观者，还是愿意长期跟随、有真实消费行为的核心人群。前者来得快去得也快，后者才能撑起长期价值。",
        ],
      },
      {
        heading: "动作三：克制合作数量与类型",
        paragraphs: [
          "热度高的时候，合作邀约会集中涌来。长期主义者反而会更谨慎地筛选，避免因为短期变现透支了形象信用。他们清楚，位置一旦被消耗，再想补回来成本极高。",
          "把这三个动作放在一起看，你会发现长期经营没有什么秘诀，它更像是一种自我约束：在流量最猛的时候，依然按计划做事。",
        ],
      },
    ],
  },
];